import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { slotOf } from '../editor/picSlot';
import { shortText } from '../print/rules';
import { api, type AiImageInfo, type AiImageSize, type MadeImage, type RecentImage } from '../sync/api';
import { $, $in } from './dom';
import { closeSheets, isOpen, openSheet } from './sheets';
import { putInSlot } from './slotFill';
import { toast } from './toast';

/*
 * รูปประกอบด้วย AI (0022, choice ข): ช่องใส่รูป → "สร้างด้วย AI" (members with the right 'ai_image', and admins).
 * The panel starts from the slot's hint; "ห้ามมีตัวหนังสือในรูป" is ticked at first (the template has its own words).
 * The price and this month's use are shown before the tap. The picture is made on the server (Gemini) and kept as the
 * person's own picture; when it comes back it goes into the slot it was made for (one undo step). If that slot is gone
 * or the page changed meanwhile, the picture stays in the panel with "ใส่เป็นรูปบนหน้านี้" — never dropped silently.
 * "รูป AI ล่าสุด" (0024, choice "เซิร์ฟเวอร์ไปรับผลเอง"): my pictures of the last 24 h — one finished while nobody waited
 * (tab closed, iPad asleep) is collected by the server; tapping it puts it in the chosen slot, without paying again.
 */
export interface AiImageHooks {
  /** a Thai reason when the open page cannot be changed now (someone else's work, a page still loading), else null */
  cannotEdit: () => string | null;
  isAdmin: () => boolean;
}

const usd = (n: number) => n < 1 ? n.toFixed(3) : n.toFixed(2);

export function initAiImage(ed: Editor, history: History, hooks: AiImageHooks) {
  const { canvas } = ed;
  const prompt = $<HTMLTextAreaElement>('aiPrompt'), noText = $in('aiNoText'), go = $<HTMLButtonElement>('aiGo');
  const out = $('aiOut'), outImg = $<HTMLImageElement>('aiOutImg');
  let info: AiImageInfo | null = null;
  let infoError = '';
  let target: fabric.Object | null = null;     // the slot the panel is for
  let promptFor: fabric.Object | null = null;  // the slot whose hint is in the box (the words typed for it are kept)
  let busy = false, started = 0, timer = 0;
  let result: { image: MadeImage; el: HTMLImageElement } | null = null;   // a picture that is still waiting to be put in

  const say = (text: string, bad = true) => { const m = $('aiMsg'); m.textContent = text; m.hidden = !text; m.classList.toggle('ok', !bad); };

  /** the slot's size on its page */
  const sizeOf = (slot: fabric.Object) => ({ w: slot.getScaledWidth(), h: slot.getScaledHeight() });
  const pictureSize = (slot: fabric.Object): AiImageSize => {
    const { w, h } = sizeOf(slot);
    return Math.max(w, h) > (info?.twoKFromPx ?? 1400) ? '2K' : '1K';
  };

  function paint() {
    const problem = info?.problem ?? null;
    const off = !info ? (infoError || 'กำลังโหลดข้อมูลรูป AI…')
      : !info.canUse ? 'บัญชีนี้ยังไม่มีสิทธิ์ "รูป AI" ขอให้ผู้ดูแลเปิดให้ที่ หลังบ้านผู้ดูแล → ผู้ใช้'
        : problem ? problem
          : info.capUsd === 0 ? 'รูป AI ปิดอยู่ (เพดานต่อเดือนตั้งไว้ 0 ดอลลาร์) ผู้ดูแลตั้งเพดานใหม่ได้ด้านล่าง'
            : null;
    const prob = $('aiProb');
    prob.textContent = off ?? ''; prob.hidden = !off || !info;
    if (info) {
      $('aiUse').textContent = `เดือนนี้ทั้งกลุ่มใช้ไป $${usd(info.usedUsd)} จากเพดาน $${usd(info.capUsd)} (นับรวมทั้งผู้ดูแลและสมาชิก เริ่มใหม่ต้นเดือนตามเวลาไทย)`;
    } else $('aiUse').textContent = infoError || 'กำลังโหลด…';
    if (target && info) {
      const size = pictureSize(target), { w, h } = sizeOf(target);
      $('aiCost').textContent = `ช่องนี้ ${Math.round(w)}×${Math.round(h)} px → รูปขนาด ${size} ราว $${usd(info.estimates[size])} ต่อรูป `
        + `(ราคารูป $${info.prices[size].toFixed(4)} + ค่าคิดของ AI · คิดจริงตามที่ Google นับ)`;
    } else $('aiCost').textContent = '';
    if (!busy) go.textContent = !target ? 'แตะช่องใส่รูปก่อน' : result ? 'สร้างอีกรูป' : 'สร้างรูป';
    go.disabled = busy || !!off || !target;
    $('aiCapRow').hidden = !hooks.isAdmin();
    if (info && document.activeElement !== $('aiCap')) $in('aiCap').value = String(info.capUsd);
    out.hidden = !result;
  }

  async function loadInfo() {
    try { info = await api.aiImage.info(); infoError = ''; }
    catch (e) { info = null; infoError = 'โหลดข้อมูลรูป AI ไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)); }
    paint();
  }

  /** "สร้างด้วย AI" on a slot */
  function open(slot: fabric.Object) {
    const why = hooks.cannotEdit();
    if (why) { toast(why, true); return; }
    if ((slot as { locked?: boolean }).locked) { toast('ช่องนี้ล็อกอยู่ ปลดล็อกที่ "เลเยอร์" ก่อน', true); return; }
    if (busy && target !== slot) { toast('กำลังสร้างรูปให้อีกช่องอยู่ รอให้เสร็จก่อน', true); openSheet('shAiImage'); return; }
    target = slot;
    if (promptFor !== slot) { prompt.value = slotOf(slot)?.hint ?? ''; promptFor = slot; noText.checked = true; }
    if (!busy) { result = null; say(''); }
    paint();
    openSheet('shAiImage');
    void loadInfo();
    void loadRecent();
  }

  /** open a picture from the server (the session cookie goes with it: same site) */
  const openPicture = (url: string) => new Promise<HTMLImageElement>((ok, no) => {
    const el = new Image();
    el.onload = () => el.naturalWidth > 0 ? ok(el) : no(new Error('รูปว่างเปล่า'));
    el.onerror = () => no(new Error('เปิดรูปจากเซิร์ฟเวอร์ไม่ได้'));
    el.src = url;
  });

  async function make() {
    if (busy || !target) return;
    const slot = target;
    const why = hooks.cannotEdit();
    if (why) { say(why); return; }
    // never pay for a picture that has nowhere to go
    if (canvas.getObjects().indexOf(slot) < 0 || !slotOf(slot)) { target = null; say('ช่องใส่รูปที่เลือกไว้ไม่อยู่บนหน้าแล้ว แตะช่องใส่รูปแล้วกด "สร้างด้วย AI" อีกครั้ง'); paint(); return; }
    const words = prompt.value.trim();
    if (!words) { say('พิมพ์คำอธิบายรูปที่อยากได้ก่อน'); prompt.focus(); return; }
    busy = true; result = null; say('');
    started = Date.now();
    // only the button's words change while waiting (CORE: never rebuild what the person may be touching)
    const tick = () => { go.textContent = `กำลังสร้าง… ${Math.round((Date.now() - started) / 1000)} วิ`; };
    tick(); timer = window.setInterval(tick, 1000);
    paint();
    try {
      const { w, h } = sizeOf(slot);
      const r = await api.aiImage.make({ prompt: words, noText: noText.checked, w, h });
      if (info) { info.usedUsd = r.usedUsd; info.capUsd = r.capUsd; }
      let el: HTMLImageElement;
      try { el = await openPicture(r.image.url); }
      catch (e) {
        say(`สร้างรูปแล้ว (คิดเงิน $${usd(r.image.costUsd)}) แต่เปิดรูปไม่ได้: ${e instanceof Error ? e.message : String(e)} · ลองเปิดแอปใหม่แล้วกดสร้างอีกครั้ง`);
        return;
      }
      const name = `รูป AI ${shortText(words, 16)}`;
      const no = putInSlot(ed, history, slot, el, name, hooks.cannotEdit);
      if (!no) {
        if (target === slot) target = null;
        toast(`ใส่รูปจาก AI แล้ว (คิดเงิน $${usd(r.image.costUsd)}) · ปรับส่วนที่เห็นได้ที่ "ครอป" · เลิกทำได้`);
        if (isOpen('shAiImage') && !matchMedia('(min-width: 900px)').matches) closeSheets();
        return;
      }
      // the slot is gone or the page changed while it was being made: keep the picture here to put in another way
      result = { image: r.image, el };
      outImg.src = r.image.url;
      say(`${no} · รูปที่ได้อยู่ด้านล่าง กด "ใส่เป็นรูปบนหน้านี้" ได้ (คิดเงิน $${usd(r.image.costUsd)} แล้ว)`);
      if (!isOpen('shAiImage')) toast('รูป AI เสร็จแล้ว แต่ช่องเดิมไม่อยู่บนหน้าแล้ว เปิด "สร้างด้วย AI" อีกครั้งเพื่อใส่รูปนั้น', true);
    } catch (e) {
      say('สร้างรูปไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)));
      if (!isOpen('shAiImage')) toast('สร้างรูป AI ไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)), true);
      void loadInfo();
    } finally {
      busy = false; window.clearInterval(timer);
      // the slot left the page while it was being made: "สร้างอีกรูป" must not make one for it
      if (target && canvas.getObjects().indexOf(target) < 0) target = null;
      paint();
      if (isOpen('shAiImage')) void loadRecent();
    }
  }

  /* ---------- รูป AI ล่าสุด (0024) ---------- */

  let recentKey = '';
  const timeOf = (iso: string) => `${iso.slice(11, 16)} น.`;   // stored in Thailand time (+07:00)
  async function loadRecent() {
    const msg = $('aiRecentMsg');
    $('aiRecent').hidden = false;
    msg.textContent = 'กำลังเช็กงาน AI ที่ค้างอยู่…';
    let list: RecentImage[];
    try { const r = await api.aiImage.recent(); list = r.images; if (info) { info.usedUsd = r.usedUsd; info.capUsd = r.capUsd; paint(); } }
    catch (e) { msg.textContent = 'โหลดรูป AI ล่าสุดไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e)); return; }
    msg.textContent = list.length ? 'แตะรูปเพื่อใส่ในช่องที่เลือก (ไม่คิดเงินซ้ำ)' : 'ยังไม่มีรูป AI ใน 24 ชั่วโมงที่ผ่านมา';
    const key = JSON.stringify(list.map(i => [i.id, i.status, i.url]));
    if (key === recentKey) return;   // the same rows: the buttons stay (CORE: never rebuild what may be touched)
    recentKey = key;
    const box = $('aiRecentList');
    box.textContent = '';
    for (const i of list) {
      const b = document.createElement('button');
      b.className = 'aithumb'; b.type = 'button'; b.dataset.status = i.status;
      b.setAttribute('aria-label', `${shortText(i.prompt, 40)} · ${timeOf(i.createdAt)}`);
      if (i.url) b.append(Object.assign(document.createElement('img'), { src: i.url, alt: '', loading: 'lazy' }));
      else b.append(Object.assign(document.createElement('span'), { textContent: i.status === 'making' ? 'กำลังสร้าง…' : i.status === 'failed' ? 'ไม่สำเร็จ' : 'ไม่มีไฟล์' }));
      b.append(Object.assign(document.createElement('small'), { textContent: timeOf(i.createdAt) }));
      b.onclick = () => void useRecent(i);
      box.append(b);
    }
  }
  async function useRecent(i: RecentImage) {
    if (i.status === 'failed') { say(`รูปนี้สร้างไม่สำเร็จ: ${i.error ?? 'ไม่ทราบเหตุผล'}`); return; }
    if (i.status === 'making') { say('รูปนี้ AI ยังทำไม่เสร็จ เปิดแผงนี้อีกครั้งภายหลัง (ไม่ต้องสร้างใหม่)', false); void loadRecent(); return; }
    if (!i.url || !i.width || !i.height) { say('รูปนี้สร้างเสร็จแต่ไม่มีไฟล์ในบันทึก สร้างใหม่ได้'); return; }
    if (busy) { say('กำลังสร้างรูปอยู่ รอให้เสร็จก่อน'); return; }
    const why = hooks.cannotEdit();
    if (why) { say(why); return; }
    let el: HTMLImageElement;
    try { el = await openPicture(i.url); }
    catch (e) { say(`เปิดรูปไม่ได้: ${e instanceof Error ? e.message : String(e)}`); return; }
    const image: MadeImage = { id: i.id, url: i.url, width: i.width, height: i.height, costUsd: i.costUsd, size: i.size, aspect: i.aspect };
    const slot = target && canvas.getObjects().indexOf(target) >= 0 && slotOf(target) ? target : null;
    const no = slot ? putInSlot(ed, history, slot, el, `รูป AI ${shortText(i.prompt, 16)}`, hooks.cannotEdit) : 'ยังไม่ได้เลือกช่องใส่รูป';
    if (!no) {
      target = null; result = null; say('');
      toast('ใส่รูป AI ลงช่องแล้ว (ไม่คิดเงินเพิ่ม) · เลิกทำได้');
      paint();
      // as after "สร้างรูป": on a phone the sheet covers the page, so it closes to show the picture
      if (!matchMedia('(min-width: 900px)').matches) closeSheets();
      return;
    }
    // no slot to put it in: the same way out as a picture whose slot went away
    result = { image, el };
    outImg.src = i.url;
    say(`${no} · รูปอยู่ด้านล่าง กด "ใส่เป็นรูปบนหน้านี้" ได้`);
    paint();
  }

  /** the waiting picture as an ordinary picture in the middle of the open page */
  function putOnPage() {
    if (!result) return;
    const why = hooks.cannotEdit();
    if (why) { say(why); return; }
    const img = new fabric.Image(result.el);
    const fitTo = Math.min(ed.doc.w, ed.doc.h) * 0.7;
    img.scale(Math.min(1, fitTo / Math.max(img.width ?? 1, img.height ?? 1)));
    img.set({ name: 'รูป AI' });
    ed.place(img);
    result = null; say('');
    toast('ใส่รูป AI บนหน้านี้แล้ว · เลิกทำได้');
    paint();
  }

  go.onclick = () => void make();
  $('aiPutPage').onclick = putOnPage;
  $('aiCapSave').onclick = async () => {
    const raw = $in('aiCap').value.replace(/[$,\s]/g, '');
    const n = Number(raw);
    // an empty box is not 0 (0 = turn AI pictures off for everyone)
    if (!raw || !Number.isFinite(n)) { say('ใส่เพดานเป็นตัวเลขดอลลาร์ (ถ้าต้องการปิดรูป AI ให้ใส่ 0)'); return; }
    try { const r = await api.aiImage.setCap(n); if (info) info.capUsd = r.capUsd; toast(`ตั้งเพดานรูป AI เป็น $${r.capUsd.toFixed(2)} ต่อเดือนแล้ว`); say(''); }
    catch (e) { say('บันทึกเพดานไม่สำเร็จ: ' + (e instanceof Error ? e.message : String(e))); }
    paint();
  };
  // the slot was removed (เลิกทำ, ลบ, เปลี่ยนหน้า) while the panel is open: say so, never make a picture for nothing
  canvas.on('object:removed', e => {
    if (e.target && e.target === target && !busy) { target = null; say('ช่องใส่รูปที่เลือกไว้ไม่อยู่บนหน้าแล้ว แตะช่องใส่รูปแล้วกด "สร้างด้วย AI" อีกครั้ง'); paint(); }
  });
  canvas.on('slot:ai' as never, ((opt: { target?: fabric.Object }) => { if (opt.target) open(opt.target); }) as never);
  return { open, loadInfo };
}
